import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Types of items that can be stored in search history
 */
export type HistoryItemType = 'cluster' | 'protein' | 'reaction' | 'compound' | 'blast'

export interface SearchHistoryItem {
  id: string
  type: HistoryItemType
  label: string
  sublabel?: string
  path: string
  timestamp: number
}

interface SearchHistoryState {
  history: SearchHistoryItem[]
  addToHistory: (item: Omit<SearchHistoryItem, 'timestamp'>) => void
  removeFromHistory: (id: string) => void
  clearHistory: () => void
  getRecentByType: (type: HistoryItemType, limit?: number) => SearchHistoryItem[]
}

const MAX_HISTORY_ITEMS = 50

export const useSearchHistoryStore = create<SearchHistoryState>()(
  persist(
    (set, get) => ({
      history: [],

      addToHistory: (item) =>
        set((state) => {
          // Remove duplicates (same id and type)
          const filtered = state.history.filter(
            (h) => !(h.id === item.id && h.type === item.type)
          )
          // Add to front with timestamp
          const newItem: SearchHistoryItem = {
            ...item,
            timestamp: Date.now(),
          }
          const updated = [newItem, ...filtered].slice(0, MAX_HISTORY_ITEMS)
          return { history: updated }
        }),

      removeFromHistory: (id) =>
        set((state) => ({
          history: state.history.filter((h) => h.id !== id),
        })),

      clearHistory: () => set({ history: [] }),

      getRecentByType: (type, limit = 10) => {
        return get()
          .history.filter((h) => h.type === type)
          .slice(0, limit)
      },
    }),
    {
      name: 'search-history-storage',
      version: 1,
    }
  )
)

/**
 * Helper to format time ago
 */
export function formatTimeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000)

  if (seconds < 60) return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`
  return new Date(timestamp).toLocaleDateString()
}
